import { copyTextToClipboard } from '@systemeio/ui'
import React from 'react'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import {
  FieldErrorAndDescription,
  FieldErrorAndDescriptionProps,
} from './form/field-error-and-description'
import { FieldLabel, FieldLabelProps } from './form/field-label'
import { LinkIcon } from '@systemeio/ui'

export type FormUrlPathInputProps = React.InputHTMLAttributes<HTMLInputElement> &
  FieldLabelProps &
  FieldErrorAndDescriptionProps & {
    domainName?: string
    additionalPath?: string
    copyButtonClassName?: string
    copyIconClassName?: string
    disabled?: boolean
    success?: boolean
    statusIcon?: React.ReactNode
    isPreFetching?: boolean
    wrapperClassName?: string
    domainClassName?: string
  }

const FormUrlPathInput = ({
  value,
  onChange,
  description,
  error,
  domainName,
  label,
  labelClassName,
  required,
  additionalPath,
  copyButtonClassName,
  copyIconClassName,
  disabled,
  success,
  statusIcon,
  isPreFetching,
  wrapperClassName,
  domainClassName,
  ...rest
}: FormUrlPathInputProps) => {
  const { t } = useLocoTranslation()
  const calculatedAdditionalPath = additionalPath ? additionalPath + '/' : ''

  const isLoading = isPreFetching

  const urlPath = `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${domainName}/${calculatedAdditionalPath}`

  return (
    <label
      className={twMerge('flex flex-col gap-1 text-xs font-medium sm:text-sm', wrapperClassName)}
    >
      <FieldLabel label={label} required={required} labelClassName={labelClassName} />
      <div className="flex">
        <div
          className={twMerge(
            'bg-background-tertiary text-text-tertiary border-y-text-secondary/50 border-l-text-secondary/50 hide-scrollbar flex shrink items-center justify-start overflow-scroll whitespace-nowrap rounded-l-lg border border-r-transparent px-2 py-2.5 sm:px-4 md:overflow-hidden md:truncate',
            domainClassName,
          )}
        >
          {domainName ? (
            urlPath
          ) : isLoading ? (
            <Loader small />
          ) : (
            `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://`
          )}
        </div>
        <input
          className={twMerge(
            'text-text-primary main-transition-colors focus:border-brand-primary w-full min-w-[100px] flex-1 rounded-none border px-4 py-2.5 focus:outline-hidden',
            error ? 'border-danger' : success ? 'border-green' : 'border-text-secondary/30',
            isLoading
              ? 'ui-bg-skeleton-primary/40 ui-text-transparent disabled:ui-bg-skeleton-primary/40'
              : 'disabled:ui-bg-background-tertiary/40',
          )}
          type={'text'}
          value={value}
          onChange={onChange}
          spellCheck={false}
          disabled={disabled || isLoading}
          {...rest}
        />
        <button
          disabled={isLoading}
          type={'button'}
          onClick={async e => {
            e.preventDefault()
            await copyTextToClipboard(`${urlPath}${value ?? ''}`, t('global.copy_clipboard'))
          }}
          className={`${copyButtonClassName} bg-brand-primary hover:bg-brand-primary-hover ring-brand-primary flex items-center justify-center rounded-r-lg px-2 py-2.5 ring-offset-1 focus:outline-hidden focus-visible:ring-2 sm:px-4`}
        >
          {statusIcon ? (
            statusIcon
          ) : (
            <LinkIcon
              className={twMerge(
                `fill-background-secondary h-[15px] w-[15px] sm:h-[20px] sm:w-[20px]`,
                copyIconClassName,
              )}
            />
          )}
        </button>
      </div>
      <FieldErrorAndDescription
        description={description}
        error={error}
        dataTestIdDescription={`${rest['data-test-id']}-description`}
        dataTestIdError={`${rest['data-test-id']}-error`}
      />
    </label>
  )
}

export default FormUrlPathInput
